import { Injectable, signal } from '@angular/core';

/**
 * `morph`: o botão "Inserir campo" do fim da folha VIRA o menu (cresce a partir dele e
 * encolhe de volta ao fechar). As outras são popovers ancorados.
 */
export type MenuPlacement = 'side' | 'below' | 'above' | 'morph';

/**
 * Menu "Inserir campo". Desde a edição inline (2026-09-25) ele é um popover ancorado
 * (position: fixed) em quem o abriu: o botão flutuante no pé da folha, a pílula entre dois
 * campos ou o card (criar acima/abaixo). Sem âncora, abre na âncora padrão, que o botão
 * flutuante registra ao nascer.
 */
@Injectable({
    providedIn: 'root',
})
export class MenuOptionsService {
    private _isMenuOptionsHidden = signal(true);
    public isMenuOptionsHidden = this._isMenuOptionsHidden.asReadonly();

    // * Fields para criar em cima ou embaixo
    public aboveFieldId = signal<string>(undefined);
    public belowFieldId = signal<string>(undefined);

    /** Elemento em que o menu está ancorado enquanto aberto. */
    public anchor = signal<HTMLElement | null>(null);

    /**
     * Onde o menu abre em relação à âncora: `side` (à esquerda, na margem cinza da folha,
     * como os cards e o "Adicionar campo"; sobre o editor só quando não há espaço), `below`
     * (dropdown) ou `above` (centralizado acima dela: o botão flutuante no pé da folha).
     */
    public placement = signal<MenuPlacement>('side');

    /**
     * O menu está aberto EM FORMA de botão (morph) e já cobre o botão real: o botão some
     * embaixo dele até o menu fechar.
     */
    public morphOpen = signal(false);

    /** Âncora de quem abre sem dizer onde (documento vazio ao carregar): o botão flutuante. */
    public defaultAnchor: HTMLElement | null = null;
    /** Onde o menu abre na âncora padrão (quem a registra diz). */
    public defaultPlacement: MenuPlacement = 'below';

    /**
     * Clica fora do menu de opções
     */
    public onMenuClickOutside(): void {
        if (!this.isMenuOptionsHidden()) {
            this.hideMenuOptions();
        }
    }

    /**
     * Mostra o menu de opções
     */
    public showMenuOptions(
        aboveFieldId?: string,
        belowFieldId?: string,
        anchor?: HTMLElement | null,
        placement: MenuPlacement = 'side'
    ): void {
        // Mostra o menu de opções
        this._isMenuOptionsHidden.set(false);

        // Define os campos para criar em cima ou embaixo
        this.aboveFieldId.set(aboveFieldId);
        this.belowFieldId.set(belowFieldId);
        this.anchor.set(anchor ?? this.defaultAnchor);
        // Sem âncora própria o menu cai na âncora padrão, no lugar que ela pediu.
        this.placement.set(anchor ? placement : this.defaultPlacement);
        // Trocou do botão que vira menu para um popover (a pílula entre campos): o botão real
        // volta a aparecer.
        if (this.placement() !== 'morph') this.morphOpen.set(false);
    }

    /**
     * Oculta o menu de opções
     */
    public hideMenuOptions(): void {
        this._isMenuOptionsHidden.set(true);
        this.morphOpen.set(false);

        // Limpa os campos para criar em cima ou embaixo
        this.aboveFieldId.set(undefined);
        this.belowFieldId.set(undefined);
        this.anchor.set(null);
    }
}
